import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase, sbError } from '@/lib/supabase';

export { useWorkspace } from '@/contexts/WorkspaceContext';

// ---- Clients --------------------------------------------------------------

export function useClients(workspaceId) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentWsRef = useRef(workspaceId);
  currentWsRef.current = workspaceId;

  const fetch = useCallback(async () => {
    if (!workspaceId) { setClients([]); setLoading(false); return; }
    const myWs = workspaceId;
    setLoading(true);
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('workspace_id', myWs)
      .order('is_pinned', { ascending: false })
      .order('name', { ascending: true });
    if (currentWsRef.current !== myWs) return;
    if (error) { setError(sbError(error)); setClients([]); }
    else { setClients(data || []); setError(null); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (payload) => {
    const { data, error } = await supabase
      .from('clients')
      .insert({ workspace_id: workspaceId, ...payload })
      .select()
      .single();
    if (error) throw new Error(sbError(error));
    await fetch();
    return data;
  }, [workspaceId, fetch]);

  const update = useCallback(async (id, patch) => {
    const { data, error } = await supabase
      .from('clients')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(sbError(error));
    await fetch();
    return data;
  }, [fetch]);

  const remove = useCallback(async (id) => {
    const { error } = await supabase.from('clients').delete().eq('id', id);
    if (error) throw new Error(sbError(error));
    await fetch();
  }, [fetch]);

  const togglePin = useCallback(async (client) => {
    return update(client.id, { is_pinned: !client.is_pinned });
  }, [update]);

  return { clients, loading, error, refresh: fetch, create, update, remove, togglePin };
}

// ---- Invoices --------------------------------------------------------------

export function useInvoices(workspaceId) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentWsRef = useRef(workspaceId);
  currentWsRef.current = workspaceId;

  const fetch = useCallback(async () => {
    if (!workspaceId) { setInvoices([]); setLoading(false); return; }
    const myWs = workspaceId;
    setLoading(true);
    const { data, error } = await supabase
      .from('invoices')
      .select('*, client:clients(id, name, ico, dic, ic_dph, address, is_pinned, email, iban)')
      .eq('workspace_id', myWs)
      .order('issue_date', { ascending: false })
      .order('created_at', { ascending: false });
    if (currentWsRef.current !== myWs) return;
    if (error) { setError(sbError(error)); setInvoices([]); }
    else { setInvoices(data || []); setError(null); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (payload) => {
    const { data, error } = await supabase
      .from('invoices')
      .insert({ workspace_id: workspaceId, ...payload })
      .select('*, client:clients(id, name, ico, dic, ic_dph, address, is_pinned, email, iban)')
      .single();
    if (error) throw new Error(sbError(error));
    await fetch();
    return data;
  }, [workspaceId, fetch]);

  const update = useCallback(async (id, patch) => {
    const { data, error } = await supabase
      .from('invoices')
      .update(patch)
      .eq('id', id)
      .select('*, client:clients(id, name, ico, dic, ic_dph, address, is_pinned, email, iban)')
      .single();
    if (error) throw new Error(sbError(error));
    await fetch();
    return data;
  }, [fetch]);

  const remove = useCallback(async (id) => {
    const { error } = await supabase.from('invoices').delete().eq('id', id);
    if (error) throw new Error(sbError(error));
    await fetch();
  }, [fetch]);

  return { invoices, loading, error, refresh: fetch, create, update, remove };
}

// ---- Expenses --------------------------------------------------------------

export function useExpenses(workspaceId) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentWsRef = useRef(workspaceId);
  currentWsRef.current = workspaceId;

  const fetch = useCallback(async () => {
    if (!workspaceId) { setExpenses([]); setLoading(false); return; }
    const myWs = workspaceId;
    setLoading(true);
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('workspace_id', myWs)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false });
    if (currentWsRef.current !== myWs) return;
    if (error) { setError(sbError(error)); setExpenses([]); }
    else { setExpenses(data || []); setError(null); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => {
    fetch();
    window.addEventListener('podnikai:expenses-changed', fetch);
    return () => window.removeEventListener('podnikai:expenses-changed', fetch);
  }, [fetch]);

  const create = useCallback(async (payload) => {
    const { data, error } = await supabase
      .from('expenses')
      .insert({ workspace_id: workspaceId, ...payload })
      .select()
      .single();
    if (error) throw new Error(sbError(error));
    window.dispatchEvent(new CustomEvent('podnikai:expenses-changed'));
    await fetch();
    return data;
  }, [workspaceId, fetch]);

  const update = useCallback(async (id, patch) => {
    const { data, error } = await supabase
      .from('expenses')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(sbError(error));
    window.dispatchEvent(new CustomEvent('podnikai:expenses-changed'));
    await fetch();
    return data;
  }, [fetch]);

  const remove = useCallback(async (id) => {
    const { error } = await supabase.from('expenses').delete().eq('id', id);
    if (error) throw new Error(sbError(error));
    window.dispatchEvent(new CustomEvent('podnikai:expenses-changed'));
    await fetch();
  }, [fetch]);

  return { expenses, loading, error, refresh: fetch, create, update, remove };
}

// ---- Documents (F-007 Document Hub) ----------------------------------------

export function useDocuments(workspaceId) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentWsRef = useRef(workspaceId);
  currentWsRef.current = workspaceId;

  const fetch = useCallback(async () => {
    if (!workspaceId) { setDocuments([]); setLoading(false); return; }
    const myWs = workspaceId;
    setLoading(true);
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('workspace_id', myWs)
      .order('created_at', { ascending: false });
    if (currentWsRef.current !== myWs) return;
    if (error) { setError(sbError(error)); setDocuments([]); }
    else { setDocuments(data || []); setError(null); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (payload) => {
    const { data, error } = await supabase
      .from('documents')
      .insert({ workspace_id: workspaceId, ...payload })
      .select()
      .single();
    if (error) throw new Error(sbError(error));
    await fetch();
    return data;
  }, [workspaceId, fetch]);

  const update = useCallback(async (id, patch) => {
    const { data, error } = await supabase
      .from('documents')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(sbError(error));
    await fetch();
    return data;
  }, [fetch]);

  const remove = useCallback(async (id) => {
    const { error } = await supabase.from('documents').delete().eq('id', id);
    if (error) throw new Error(sbError(error));
    await fetch();
  }, [fetch]);

  return { documents, loading, error, refresh: fetch, create, update, remove };
}

// ---- Invoice Presets -------------------------------------------------------

export function useInvoicePresets(workspaceId) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentWsRef = useRef(workspaceId);
  currentWsRef.current = workspaceId;

  const fetch = useCallback(async () => {
    if (!workspaceId) { setPresets([]); setLoading(false); return; }
    const myWs = workspaceId;
    setLoading(true);
    const { data, error } = await supabase
      .from('invoice_presets')
      .select('*, client:clients(id, name, ico, dic, ic_dph, address, is_pinned, email, iban)')
      .eq('workspace_id', myWs)
      .order('updated_at', { ascending: false });
    if (currentWsRef.current !== myWs) return;
    if (error) { setError(sbError(error)); setPresets([]); }
    else { setPresets(data || []); setError(null); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (payload) => {
    const { data, error } = await supabase
      .from('invoice_presets')
      .insert({ workspace_id: workspaceId, ...payload })
      .select('*, client:clients(id, name, ico, dic, ic_dph, address, is_pinned, email, iban)')
      .single();
    if (error) throw new Error(sbError(error));
    await fetch();
    return data;
  }, [workspaceId, fetch]);

  const update = useCallback(async (id, patch) => {
    const { data, error } = await supabase
      .from('invoice_presets')
      .update(patch)
      .eq('id', id)
      .select('*, client:clients(id, name, ico, dic, ic_dph, address, is_pinned, email, iban)')
      .single();
    if (error) throw new Error(sbError(error));
    await fetch();
    return data;
  }, [fetch]);

  const remove = useCallback(async (id) => {
    const { error } = await supabase.from('invoice_presets').delete().eq('id', id);
    if (error) throw new Error(sbError(error));
    await fetch();
  }, [fetch]);

  return { presets, loading, error, refresh: fetch, create, update, remove };
}

// ---- Helper: next invoice number ------------------------------------------
export function formatInvoiceNumber({ counter, format }) {
  const fmt = format || '{YYYY}{NNN}';
  const n = Number(counter) || 0;
  const yyyy = new Date().getFullYear();
  return fmt
    .replace('{YYYY}', String(yyyy))
    .replace('{YY}', String(yyyy).slice(-2))
    .replace('{NNNN}', String(n).padStart(4, '0'))
    .replace('{NNN}', String(n).padStart(3, '0'))
    .replace('{NN}', String(n).padStart(2, '0'))
    .replace('{N}', String(n));
}

export function nextInvoiceNumber(workspace) {
  return formatInvoiceNumber({
    counter: (workspace?.fa_counter || 0) + 1,
    format: workspace?.fa_number_format,
  });
}
